import React, { useEffect, useState } from 'react'
import {
    Area,
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts'

const LINE_COLOR = '#308089'
const BAR_COLORS = ['#E7FC67', '#9FF365', '#33C0A4', '#A8DCD9', '#ECA089']

export function formatNumberScale(number, usd = false, decimals = 0) {
  const sign = number < 0 ? '-' : ''
  const absNumber = Math.abs(number)

  if (isNaN(absNumber) || absNumber === '' || absNumber === undefined) {
    return sign + (usd ? '$0.00' : '0')
  }
  const num = parseFloat(absNumber)
  const fullNum = Math.floor(num).toLocaleString('fullwide', {
    useGrouping: false,
  })
  const wholeNumberLength = fullNum.length

  if (wholeNumberLength >= 19) return sign + (usd ? '$1000 Q' : '1000 Q')
  if (wholeNumberLength >= 16)
    return (
      sign +
      (usd ? '$' : '') +
      (num / Math.pow(10, 15)).toFixed(decimals) +
      ' Q'
    )
  if (wholeNumberLength >= 13)
    return (
      sign +
      (usd ? '$' : '') +
      (num / Math.pow(10, 12)).toFixed(decimals) +
      ' T'
    )
  if (wholeNumberLength >= 10)
    return (
      sign + (usd ? '$' : '') + (num / Math.pow(10, 9)).toFixed(decimals) + ' B'
    )
  if (wholeNumberLength >= 7)
    return (
      sign + (usd ? '$' : '') + (num / Math.pow(10, 6)).toFixed(decimals) + ' M'
    )
  if (wholeNumberLength >= 4)
    return (
      sign + (usd ? '$' : '') + (num / Math.pow(10, 3)).toFixed(decimals) + ' K'
    )

  if (num < 0.0001 && num > 0) {
    return sign + (usd ? '< $0.0001' : '< 0.0001')
  }

  return sign + (usd ? '$' : '') + num.toFixed(decimals)
}

const ZeroCenteredBarChart = ({
  data,
  xDataKey,
  yDataKeys,
  legend,
  colors,
  format,
}) => {
  const [chartData, setChartData] = useState([])

  if (colors === undefined) {
    colors = BAR_COLORS
  }

  const barValues = chartData.flatMap((item) => [
    item[yDataKeys[0]],
    item[yDataKeys[1]],
  ])
  const areaValues = chartData.flatMap((item) => [
    item[yDataKeys[2]],
    item[yDataKeys[3]],
  ])

  const maxBarValue = Math.max(...barValues.map(Math.abs))
  const maxAreaValue = Math.max(...areaValues.map(Math.abs))

  const barDomain = [-maxBarValue, maxBarValue]
  const areaDomain = [-maxAreaValue, maxAreaValue]

  useEffect(() => {
    if (!data.length) {
      return
    }

    const newData = data.map((item) => ({
      ...item,
      total: item[yDataKeys[2]] + item[yDataKeys[3]],
    }))

    setChartData(newData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <ResponsiveContainer width="95%" height={400}>
      <ComposedChart data={chartData} debounce={1}>
        <Legend layout="horizontal" verticalAlign="bottom" align="center" />
        <Bar
          name={legend[0]}
          dataKey={yDataKeys[0]}
          fill={colors[0]}
          stackId="a"
        />
        <Bar
          name={legend[1]}
          dataKey={yDataKeys[1]}
          fill={colors[1]}
          stackId="b"
        />

        <CartesianGrid
          horizontal={true}
          vertical={true}
          opacity={0.2}
          strokeDasharray="8"
        />
        <XAxis dataKey={xDataKey} />

        <YAxis
          yAxisId="right"
          orientation="right"
          domain={areaDomain}
          label={{ fill: LINE_COLOR }}
          tick={{ stroke: LINE_COLOR }}
          tickLine={{ stroke: LINE_COLOR }}
          fontSize="14px"
          tickFormatter={
            format === false
              ? (data) => {
                  return data
                }
              : formatNumberScale
          }
        />
        <YAxis
          domain={barDomain}
          tick={{ stroke: 'white' }}
          fontSize="14px"
          tickFormatter={
            format === false
              ? (data) => {
                  return data
                }
              : formatNumberScale
          }
        />

        <Area
          type="monotone"
          name={legend[2]}
          dataKey={yDataKeys[2]}
          stroke={colors[0]}
          fill={colors[0]}
          fillOpacity={0.2}
          connectNulls
          yAxisId="right"
        />

        <Area
          type="monotone"
          name={legend[3]}
          dataKey={yDataKeys[3]}
          stroke={colors[1]}
          fill={colors[1]}
          fillOpacity={0.2}
          connectNulls
          yAxisId="right"
        />

        <Line
          type="monotone"
          name="Traders Net PnL"
          dataKey="total"
          stroke="#F0DE36"
          dot={false}
          yAxisId="right"
        />

        <Tooltip
          contentStyle={{
            backgroundColor: '#05113b',
            border: 'none',
            borderRadius: '12px',
            fontSize: '12px',
          }}
          formatter={
            format === false
              ? (data) => {
                  return data
                }
              : formatNumberScale
          }
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export default ZeroCenteredBarChart
